import { Button as ButtonAntd } from 'antd'
import { clsx } from 'clsx'
import { useFocus } from 'hook/useFocus'
import { useLatestCallback } from 'hook/useLatestCallback'
import { ClickAction, useOnClickAction } from 'hook/useOnClickAction'
import { Icon } from 'icon/Icon'
import { CSSProperties, ForwardedRef, forwardRef, MouseEvent, ReactNode } from 'react'
import { opt } from 'store/opt'
import { Gap, getGapStyle, getPaddingStyle, Padding, styles } from 'util/style'
import css from './Button.module.scss'

type ButtonType = 'default' | 'primary' | 'dashed' | 'link' | 'text'

type Props = {
  action?: ClickAction | false
  active?: boolean
  children?: ReactNode
  className?: string
  compact?: boolean
  dashed?: boolean
  disabled?: boolean
  error?: boolean
  flat?: boolean
  height?: number | string
  icon?: ReactNode
  large?: boolean
  left?: boolean
  link?: boolean
  onClick?(event: MouseEvent): void
  onFocus?(focus: boolean): void
  onMouseDown?(event: MouseEvent): void
  text?: boolean
  blur?: boolean
  pad?: Padding
  round?: number | boolean
  gap?: Gap
  primary?: boolean
  small?: boolean
  style?: CSSProperties
  title?: string
  type?: ButtonType
  wide?: boolean
  square?: boolean
  upper?: boolean
  width?: number | string
  size?: number | string
}

export const Button = forwardRef((props: Props, ref: ForwardedRef<HTMLButtonElement>) => {
  const {
    active, children, className, compact, dashed, disabled, error, round,
    icon, large, left, link, text, flat, blur, primary, small, title, wide, square, upper,
  } = props
  const { busy, onClick } = useOnClickAction(props)
  const { onFocus, onBlur } = useFocus(props)

  const type = props.type ? props.type : primary ? 'primary' : dashed ? 'dashed' :
    text ? 'text' : flat ? 'text' : blur ? 'text' : link ? 'link' : undefined
  const size = large ? 'large' : small ? 'small' : undefined
  const classes = clsx(className, css.button, opt.tsum && css.tsum, busy && 'ant-btn-loading',
    opt.stand && css.stand, opt.basic && css.basic, active && css.active, compact && css.compact,
    (square || opt.square) && css.square, round && css.round, left && css.left, link && css.link, flat && css.flat,
    blur && css.blur, small && css.small, large && css.large, wide && css.wide, upper && css.upper, error && css.error)
  const pad = getPaddingStyle(props.pad)
  const gap = getGapStyle(props.gap)
  const borderRadius = typeof round === 'number' ? opt.radius(round) : undefined
  const width = props.width ?? props.size
  const height = props.height ?? props.size
  const style = styles({ width, height, borderRadius, ...pad, ...gap, ...props.style })
  const _icon = !icon ? null : busy ? <Icon size={16} name="progress_activity" /> : icon

  const onMouseDown = useLatestCallback((event: MouseEvent) => {
    props.onMouseDown?.(event)
  })

  return <ButtonAntd ref={ref} type={type} size={size} icon={_icon} disabled={disabled} danger={error}
    className={classes} style={style} onClick={onClick} onMouseDown={onMouseDown}
    title={title} onFocus={onFocus} onBlur={onBlur}>
    {children}
  </ButtonAntd>
})
